import * as R from 'ramda'
import { MouseEvent, ReactNode, memo, useMemo } from 'react'
import { Divider, useTheme } from '@mui/material'
import { RoomTypes } from '@weblite/ts-action/room'
import { EmbeddedLink, IMessage } from '@weblite/ts-action/message'
import { useDraft } from '$logic/draft/react-hook'
import { useRoomPendingMessagesById } from '$logic/pending-messages/react-hook'
import { useRoomPendingUploadsById } from '$logic/pending-uploads/react-hook'
import { useLongPress } from '$hooks/use-long-press'
import { nothing } from '$helper/functions/utils'
import { Title } from './components/title'
import { DateFormatter } from './components/date-formatter'
import { MessageType } from './components/message-type'
import { Message } from './components/message'
import { Status } from './components/status'
import { MessageDraftPrefix } from './components/message-draft'
import './styles.scss'

// TODO: CHECK THESE PROPS IF THEY ARE CORRECTLY REQUIRED OR NOT
// TODO: Import MessageType from logic
interface Props {
  chatId: string
  pinned: boolean
  selected: boolean
  roomType?: RoomTypes
  title: string
  verified?: boolean
  date?: number
  lastMessageSenderIcon?: ReactNode
  lastMessageSenderName?: ReactNode
  lastMessageType?: IMessage['type']
  lastMessageBody: string
  fromMe?: boolean
  muted?: boolean
  seen?: boolean
  received?: boolean
  unseen?: number
  eventTitle?: ReactNode
  messageAction?: ReactNode
  avatarElement?: ReactNode
  hasDeleted?: boolean
  hasMarkAsUnread: boolean
  hasMention?: boolean
  isOnTop?: boolean
  isMookeb?: boolean
  links?: EmbeddedLink[]
  onClick: (chatId: string) => void
  onContextMenu: (chatId: string) => (e: MouseEvent) => void
  onLongPress?: (chatId: string) => () => void
  isOpened: boolean | undefined
}

function BorderElement() {
  const background = useTheme().palette.neutral700

  return (
    <div
      style={{
        width: '4px',
        background,
        height: '100%',
        position: 'absolute',
        right: 0,
      }}
    />
  )
}
// TODO: IMPLEMENT LOGIC FOR EVENT AND DRAFT IN PARENTS
export function ChatListItemBaseComponent({
  chatId,
  pinned,
  selected,
  roomType,
  title,
  verified = false,
  date,
  lastMessageSenderIcon = null,
  lastMessageSenderName = null,
  lastMessageType,
  lastMessageBody,
  fromMe = false,
  muted = false,
  seen = false,
  received = false,
  unseen = 0,
  eventTitle = null,
  messageAction = null,
  avatarElement = null,
  hasDeleted = false,
  hasMarkAsUnread,
  hasMention = false,
  isOnTop,
  isMookeb = false,
  links,
  onClick,
  onContextMenu,
  onLongPress = () => nothing,
  isOpened = false,
}: Props) {
  const theme = useTheme()
  const longPressHandlers = useLongPress(onLongPress?.(chatId) || nothing)

  // TODO : MOVE USE_DRAFT TO CHAT-LIST-ITEM-BASE
  const draftEntity = useDraft(chatId)
  const draft = draftEntity?.value?.body

  const pendingMessages = useRoomPendingMessagesById(chatId)
  const pendingUploads = useRoomPendingUploadsById(chatId)

  const pendingContent = useMemo(
    () =>
      R.head(
        R.sort(R.ascend(R.prop('pendingDate')), [
          ...pendingMessages,
          ...pendingUploads,
        ]),
      ),
    [pendingUploads, pendingMessages],
  )

  const lastMessageIsPending =
    pendingContent?.pendingDate && pendingContent?.pendingDate > (date || 0)
  const lastVisibleBody = lastMessageIsPending
    ? pendingContent?.body
    : lastMessageBody

  const lastVisibleDate = unseen
    ? date
    : draftEntity?.date
    ? draftEntity.date
    : lastMessageIsPending
    ? pendingContent?.pendingDate
    : date

  const lastVisibleMessageType = (lastMessageIsPending
    ? pendingContent?.type
    : lastMessageType) as IMessage['type']

  const showLastMessageSenderInfos = !messageAction && (unseen || !draft)
  const showStatusBar = Boolean(unseen || !draft)

  return (
    <>
      <button
        type="button"
        className="chat-list-item--root"
        onClick={() => onClick(chatId)}
        onContextMenu={onContextMenu(chatId)}
        //  eslint-disable-next-line react/jsx-props-no-spreading
        {...longPressHandlers}
        style={{
          backgroundColor: selected
            ? theme.palette.primary100
            : isOpened
            ? 'rgba(80, 91, 95, 0.10)'
            : pinned || isOnTop
            ? theme.palette.primary50 // Pinned Color
            : theme.palette.white,
        }}
      >
        {isOpened && <BorderElement />}
        {avatarElement}

        <div className="chat-list-item--body">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '2px',
            }}
          >
            <Title
              roomType={roomType}
              title={title}
              verified={verified}
              hasDeleted={hasDeleted}
              isMookeb={isMookeb}
            />
            {lastVisibleDate && <DateFormatter time={lastVisibleDate} />}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              textAlign: 'right',
            }}
          >
            {showLastMessageSenderInfos && (
              <>
                {!lastMessageIsPending && lastMessageSenderIcon}
                {!lastMessageIsPending && lastMessageSenderName}
                <MessageType type={lastVisibleMessageType} />
              </>
            )}
            {!messageAction && !unseen && <MessageDraftPrefix draft={draft} />}
            {!messageAction && !draft && eventTitle}

            <Message
              type={lastVisibleMessageType}
              body={lastVisibleBody}
              draft={draft}
              messageAction={messageAction}
              hasUnseen={!!unseen}
              links={links}
            />

            <Status
              fromMe={fromMe}
              pinned={pinned}
              muted={muted}
              seen={seen}
              received={received}
              unseen={unseen}
              isPending={!!lastMessageIsPending}
              showStatusBar={showStatusBar}
              hasMarkAsUnread={hasMarkAsUnread}
              hasMention={hasMention}
            />
          </div>
        </div>
      </button>

      <Divider
        variant={selected ? undefined : 'inset'}
        sx={{
          borderColor: selected ? 'secondary.light' : 'veryVeryLightGray',
        }}
      />
    </>
  )
}

export const ChatListItemBase = memo(ChatListItemBaseComponent)
